import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Info, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createGAPixel } from "@/lib/api";
import PortalNotesFields from "@/components/PortalNotesFields";

export default function GAPixelBuilder() {
  const nav = useNavigate();
  const [name, setName] = useState("GA Homepage Pixel");
  const [gaId, setGaId] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [portals, setPortals] = useState([]);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!gaId.trim() || !apiSecret.trim()) {
      toast.error("Measurement ID and API Secret are both required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createGAPixel({
        name: name.trim(),
        ga_measurement_id: gaId.trim(),
        ga_api_secret: apiSecret.trim(),
        portals, notes, tags, folder_id: folderId,
      });
      toast.success("GA4 pixel created");
      nav(`/success/ga_pixel/${res.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">New Component</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
            Google Analytics Pixel
          </h1>
          <p className="text-zinc-600 mt-2">
            Server-side forwards every hit to GA4 via the Measurement Protocol — no client-side gtag.js required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <form data-testid="builder-form" onSubmit={onSubmit} className="md:col-span-7 space-y-8">
            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Pixel</div>
              <div>
                <Label className="text-xs text-zinc-500">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ga-pixel-name-input"
                  className="mt-1.5" />
              </div>
            </section>

            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="flex items-center justify-between">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">GA4 Credentials</div>
                <a href="https://developers.google.com/analytics/devguides/collection/protocol/ga4/sending-events?client_type=gtag#send_an_event"
                   target="_blank" rel="noopener noreferrer"
                   className="text-[11px] text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1">
                  Where do I find these? <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Measurement ID</Label>
                <Input value={gaId} onChange={(e) => setGaId(e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  className="mt-1.5 font-mono"
                  data-testid="ga-measurement-id-input" />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  GA4 → Admin → Data streams → your stream → Measurement ID.
                </p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 flex items-center gap-1.5">
                  API Secret <ShieldCheck className="h-3 w-3 text-green-600" />
                </Label>
                <Input type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Never displayed after save"
                  className="mt-1.5 font-mono"
                  data-testid="ga-api-secret-input" />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Same stream → Measurement Protocol API secrets → Create. Stored on your server, never shipped to browsers.
                </p>
              </div>
              <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-[11px] text-blue-900">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>GA4 pixels don't store hit details in PortalStudio — everything goes straight to your Google Analytics property. Use a <strong>PortalStudio Pixel</strong> if you want first-party analytics here.</span>
              </div>
            </section>

            <PortalNotesFields portals={portals} setPortals={setPortals}
                              notes={notes} setNotes={setNotes}
                              tags={tags} setTags={setTags}
                              folderId={folderId} setFolderId={setFolderId} />

            <Button type="submit" disabled={submitting} data-testid="generate-embed-btn"
              className="h-12 px-6 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create GA4 pixel
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
